import { translate } from '../../core/i18n';
import { getComponentData, setComponentData } from '../../core/views';
import { CONSTANTS, LOGIN_REG_MODAL_STATE, ONBOARDING_STATE, QUESTION_MODAL_STATE } from '../../core/constants';
import { getDomainCfg, store } from '../../core/state';
import { dismissLogin, resetDismissedLogin, wasLoginDismissed } from '../../features/login';
import { addVisit, completedApps, firstVisit } from '../../features/stats';
import { CLOUD_USER, getCloudUser } from '../../features/cloudstorage/cloud';
import { cleanedUserName, loginUser, registerUser } from '../../features/cloudstorage/user';

import { BaseModal } from './BaseModal';
import { useEffect, useRef, useState } from 'react';
import { cleanLocalProjects, migrateLocalStorageProjectsToCloud } from '../../features/localstorage/clean';
import { loadProjectList } from '../../core/code/loader';
import { getHasCodingCards, setCurrentOnboardingStep } from '../../features/onboarding';
import { redirectToProjectListRequested } from '../../features/redirect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CloudUser } from '../../core/types';
import { removeAchievementsFromLS } from '../../features/gamification/events';

import { hideOnboarding, showOnboarding } from '../Onboarding';

export const triggerLoginModal = () : void => {
  if (!wasLoginDismissed() && (getDomainCfg().product.activationUrl !== undefined || getDomainCfg().product.private === true)) {
    showLoginRegisterModal(true);
  }
}

export const hideLoginModal = (): void => {
  setComponentData(LOGIN_REG_MODAL_STATE, { data: { show: false } });
  //check if dismissed needed
}

export const getSpecialMessage = () => {
  if (getDomainCfg().product.private === true) {
    return translate("view-portal-question-title", getDomainCfg().product.name);
  }
  return getDomainCfg().product.activationUrl !== undefined ? translate("view-activation-title", getDomainCfg().product.name) : undefined;
}

export const showLoginRegisterModal = (login : boolean, welcome?: boolean): void => {
  resetDismissedLogin();
  const msg = getSpecialMessage();
  const modalState = getComponentData(LOGIN_REG_MODAL_STATE);
  const registrationUrl = getDomainCfg().product.registrationUrl;
  const hasRegistrationFunnel = typeof registrationUrl === 'string' && registrationUrl.trim().length > 0;
  modalState.data = {
    ...modalState.data,
    msg,
    show: true,
    initialized : false,
    showLogin : login,
    showRegister : (!login && welcome !== true) && getDomainCfg().viewSettings.features.registrationAvailable && !hasRegistrationFunnel,
    showFirstTimeButton : welcome === true
  };
  setComponentData(LOGIN_REG_MODAL_STATE, modalState);
}


const calcWelcomeState = (data : any, user : CloudUser) => {  
  const registrationUrl = getDomainCfg().product.registrationUrl;
  const hasRegistrationFunnel = typeof registrationUrl === 'string' && registrationUrl.trim().length > 0;
  if (getDomainCfg().product.private === true) {
    return {
      ...data,
      msg : translate("view-portal-question-title", getDomainCfg().product.name),
      showLogin : true,
      showRegister : false,
      showFirstTimeButton : false
    }
  }
  if (getDomainCfg().product.activationUrl !== undefined) {
    return {
      ...data,
      msg : translate("view-activation-title", getDomainCfg().product.name),
      showLogin : true,
      showRegister : false,
      showFirstTimeButton : false
    }
  }
  if (firstVisit() === false) {
    if (getCloudUser().loggedIn === false) {
      if (completedApps() === 1) {
        return {
          ...data,
          msg : translate("login-welcome-back-2"),
          showLogin : false,
          showRegister : getDomainCfg().viewSettings.features.registrationAvailable && !hasRegistrationFunnel,
          showFirstTimeButton : false
        }
      }
      return {
        ...data,
        msg : translate("login-welcome-back"),
        showLogin : true,
        showRegister : false,
        showFirstTimeButton : false
      }
    }
    setTimeout(hideLoginModal, 1000);
    return {
      ...data,
      msg : translate("login-welcome-back-user", cleanedUserName()),
      showLogin : false,
      showRegister : false,
      showFirstTimeButton : false
    };
  }
  return {
    ...data,
    msg : '',
    showLogin : false,
    showRegister : false,
    showFirstTimeButton : data?.showFirstTimeButton
  };
}

export const LoginRegisterModal = () => {
  const [modalState, setModalState] = store.useState(LOGIN_REG_MODAL_STATE);
  const [loginFormData, setLoginFormData] = useState({account:"", password:""});
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showLoginErrorAlert, setShowLoginErrorAlert] = useState(false);
  const [showRegisterErrorAlert, setShowRegisterErrorAlert] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [cloudUser] = store.useState(CLOUD_USER);

  //const stepPopperState = getComponentData(STEP_POPPER);
  const loginFormRef = useRef(null); // Ref für das Login-Formular erstellen

  useEffect(() => {
    setTimeout(() => {      

      if (modalState.data?.showLogin) {        
        hideOnboarding(); // StepPopper verstecken
        loginFormRef.current?.focus(); // Fokus auf das Account-Eingabefeld setzen        
      } else {      
        showOnboarding(); // StepPopper aktivieren        
      }
    }, 300);
  }, [modalState.data?.showLogin]);

  const showLoginFormRequested = (): void => {
    const newState = { ...modalState }
    newState.data = {
      ...modalState.data,
      show : true,
      showLogin : true,
      showRegister : false
    }
    setModalState(newState);
    setShowSuccessAlert(false);
    setShowLoginErrorAlert(false);
    setShowRegisterErrorAlert(false);
  }

  const showRegisterFormRequested = (): void => {
    const newState = { ...modalState }
    newState.data = {
      ...modalState.data,
      show : true,
      showLogin : false,
      showRegister : true
    }
    setModalState(newState);
    setShowSuccessAlert(false);
    setShowLoginErrorAlert(false);
    setShowRegisterErrorAlert(false);
  }

  const changedAccount = (e: { target: { value: string; }; }) => {
    const formData = { ...loginFormData };
    formData.account = e.target.value;
    setLoginFormData(formData);
  }

  const changedPassword = (e: { target: { value: string; }; }) => {
    const formData = { ...loginFormData };
    formData.password = e.target.value;
    setLoginFormData(formData);
  }

  const loginRequested = async (e : any) : Promise<void> => {
    e.preventDefault();
    try {
      await loginUser(loginFormData.account, loginFormData.password);
      await loadProjectList();
    } catch (err) {
      setShowSuccessAlert(false);
      setShowLoginErrorAlert(true);
      setShowRegisterErrorAlert(false);
      return;
    }

    cleanLocalProjects();
    removeAchievementsFromLS();
    setShowSuccessAlert(true);
    setShowLoginErrorAlert(false);
    setShowRegisterErrorAlert(false);
    //showToast(translate("login-success-toast-title"), translate("login-success-toast-message"));
    if (getDomainCfg().viewSettings.features.hasOnboarding 
      && getDomainCfg().product.private 
      && getComponentData(ONBOARDING_STATE).currentStep < 0) {
      setCurrentOnboardingStep(1);
    }
    setTimeout(abortRequested, 3000);
  }

  const registerRequested = async (e : any) : Promise<void> => {
    e.preventDefault();
    try {
      if (!getDomainCfg().viewSettings.features.registrationAvailable || getDomainCfg().product.private === true) return;
      await registerUser(loginFormData.account, loginFormData.password, loginFormData.password);
    } catch (err) {
      setShowSuccessAlert(false);
      setShowLoginErrorAlert(false);
      setShowRegisterErrorAlert(true);
      return;
    }
    await loginUser(loginFormData.account, loginFormData.password);
    migrateLocalStorageProjectsToCloud();
    setShowSuccessAlert(true);
    setShowLoginErrorAlert(false);
    setShowRegisterErrorAlert(false);
    setTimeout(abortRequested, 3000);
  }

  const abortRequested = (): void => {
    dismissLogin();
    setModalState({ data: { show: false } });
    setShowSuccessAlert(false);
    setShowLoginErrorAlert(false);
    setShowRegisterErrorAlert(false);
  }

  const handleFirstTime = (): void => {
    setCurrentOnboardingStep(1);
    redirectToProjectListRequested();
    abortRequested();
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    //console.log('LoginModal: modal state changed', modalState);
    if (!modalState.data || (modalState?.data?.show === true && modalState?.data?.initialized === false && !modalState?.data?.showLogin && !modalState?.data?.showRegister)) {
      const newState = { ...modalState };
      newState.data = calcWelcomeState(newState.data, cloudUser);
      newState.data.initialized = true;
      const onboardingStep = getComponentData(ONBOARDING_STATE).currentStep;
      const onboardingActive = getDomainCfg().viewSettings.features.hasOnboarding && onboardingStep > -1 ;
      const otherModals = getComponentData(QUESTION_MODAL_STATE);
      if (getDomainCfg().viewSettings.features.showFirstTimeModalOnStart && !getCloudUser()?.loggedIn && !otherModals?.data?.show && !onboardingActive && getHasCodingCards()) {        
        newState.data.show = true;        
      } else {
        newState.data.show = false;
      }
      addVisit();
      setModalState(newState);
      //console.log('set modal state', newState);
    }
  }, [modalState, cloudUser])  

  if (wasLoginDismissed()) return null;
  if (modalState.data?.show !== true) return null;

  const domainCfg = getDomainCfg();
  const logoImagePath = domainCfg.product.logoUrl;
  const accountName = domainCfg.product.accountName;
  const productName = domainCfg.product.name;
  const deVersion = domainCfg.lang === 'de';
  const schoolVersion = domainCfg.product.private === true;

  const firstTimeButton = (<div id="firstform " className="text-center">
    <button className="btn btn-go btn-block yellowColorBtn" id="first" onClick={handleFirstTime}>{translate("view-login-start")}</button>
  </div>);

  const successMsg = modalState?.data?.showLogin ? translate("view-login-success") : translate("view-signup-success");
  const successAlert = (<div className="alert alert-success" id="signupAlert">
    <p>{successMsg}</p>
  </div>);

  const loginAlert = (<div className="alert alert-danger" id="loginAlert">
    <p dangerouslySetInnerHTML={{ __html : translate("view-login-failed") }} />
    <a href={getDomainCfg().product.passwordResetLink}>{ translate("login-passwort-lost") }</a>
  </div>);

  const showActivationUrl = !schoolVersion && getDomainCfg().product.activationUrl !== undefined;
  const activationLoginText = showActivationUrl ? translate("view-activation-login") : undefined;

  let loginText = getDomainCfg().product.private ? translate("view-school-login") : translate("view-login-login-hint", accountName);
  if (activationLoginText) {
    loginText = activationLoginText;
  }
  const loginButton = (<div className="text-center">
    <p>{loginText}</p>
    <button id="login-form-btn" className="redColorBtn btn btn-primary btn-go" onClick={showLoginFormRequested}>Login</button>
  </div>);

  const passwordShowIcon = showPassword ? 'eye-slash' : 'eye';

  const loginForm = (
    <div>
      <div id="loginform" className="loginform text-center">
        <form onSubmit={loginRequested}>
          <fieldset>
            <legend> {loginText} </legend>
            <div className="input-group">
              <span className="input-group-text">{translate("view-login-account")}</span>
              <input type="text" 
                     name="name" 
                     autoComplete="username" 
                     aria-label={translate("view-login-account")} 
                     className="form-control" 
                     placeholder={translate("view-login-user-placeholder")}
                     required 
                     onChange={changedAccount} 
                     minLength={CONSTANTS.USER_NAME_MIN} 
                     maxLength={CONSTANTS.USER_NAME_MAX}
                     ref={loginFormRef} 
                   />
            </div>
            <div className="input-group mt-1">
              <span className="input-group-text">{translate("login-passwort")}</span>
              <input type={showPassword ? 'text' : 'password'}
                     name="password"
                     autoComplete="current-password"
                     aria-label="password"
                     className="form-control"
                     placeholder={translate("view-login-password-placeholder")}
                     title={translate("view-login-password-chars", CONSTANTS.WP_INPUT_ALLOW_CHARS)}                     
                     required onChange={changedPassword} 
                     minLength={CONSTANTS.PASSWORD_MIN} 
                     maxLength={CONSTANTS.PASSWORD_MAX} 
                     pattern={CONSTANTS.WP_INPUT_REGEX_STR} />
              <div className="input-group-append">
                <button type="button" 
                        className="btn" 
                        onClick={togglePasswordVisibility} 
                        title={translate("view-login-password-button", CONSTANTS.WP_INPUT_ALLOW_CHARS)}                     
                        >
                  <FontAwesomeIcon icon={passwordShowIcon} />
                </button>
              </div>
            </div>
          </fieldset>

          <input type="submit" id="login-btn" className="btn btn-go redColorBtn mt-1" value={translate("view-login-text")} />
        </form>
        { showLoginErrorAlert && loginAlert }
      </div>
    </div>);

  const cancelButton = (<div id="close" className="full-width form-group text-end" >
    <p data-dismiss="modal" onClick={abortRequested}><small><u>{translate("view-login-close-window")}</u></small> </p>
  </div>);

  const registerAlert =(<div className="alert alert-danger" id="signupFail">
    <p> { translate("login-signup-failed-title") }<br /><span>{ translate("login-signup-failed-text") }</span></p>
  </div>);

  const registerButton = (<div className="text-center">
    <p>{translate("view-login-register", accountName)}</p>
    <button id="register-form-btn" className="redColorBtn btn btn-primary btn-go" onClick={showRegisterFormRequested}>{translate("view-login-register-action")}</button>
  </div>);
  
  const registerForm = (<div className="registerform text-center">
    <form onSubmit={registerRequested}>
      <fieldset>
        <legend dangerouslySetInnerHTML={{ __html : translate("view-login-signup", getDomainCfg().product.accountName) }} />
        <div className="input-group">
          <input type="email" 
                name="email" 
                id="signupemail" 
                className="form-control"
                autoComplete="username" 
                placeholder={translate("view-login-signup-mail")}
                required 
                onChange={changedAccount} 
                minLength={CONSTANTS.USER_NAME_MIN} 
                maxLength={CONSTANTS.USER_NAME_MAX} />
        </div>
        <div className="input-group mt-1">
          <input type={showPassword ? 'text' : 'password'}
                name="password" 
                id="signuppass" 
                className='form-control'
                autoComplete="new-password" 
                placeholder={translate("view-login-password-placeholder")}
                required 
                onChange={changedPassword} 
                minLength={CONSTANTS.PASSWORD_MIN} 
                maxLength={CONSTANTS.PASSWORD_MAX} 
                pattern={CONSTANTS.WP_INPUT_REGEX_STR} />
          <button type="button" 
                    className="btn" 
                    onClick={togglePasswordVisibility} 
                    title={translate("view-login-password-button", CONSTANTS.WP_INPUT_ALLOW_CHARS)}                     
                    >
            <FontAwesomeIcon icon={passwordShowIcon} />
          </button>
        </div>  

      </fieldset>
      <button id="register-btn" className="btn btn-submit redColorBtn">{translate("view-login-register-action")}</button>
    </form>
    { showRegisterErrorAlert && registerAlert }
  </div>);

  const body = (<>
    <p className="h3"><img src={logoImagePath} className="imgcenter productLogo" width="281" max-height="57" alt="logo" /></p>
    <h3 id="msg" className="text-center">{modalState?.data?.msg}</h3>
    { showSuccessAlert && successAlert }
    { modalState?.data?.showFirstTimeButton && firstTimeButton }
    { !modalState?.data?.showLogin && loginButton }
    { modalState?.data?.showLogin && loginForm }
  </>);

  const register = <>
    { !modalState?.data?.showRegister && registerButton }
    { (modalState?.data?.showRegister) && registerForm}
  </>

  const showPrivateUrl = !schoolVersion && getDomainCfg().product.privateUrl !== undefined;
  const privateUrl = getDomainCfg().product.privateUrl;
  const privateLinkText = <div style={{width:"100%"}} className='text-center'>
    <hr />
    <a href={privateUrl} target='_blank'>{translate('view-private-link')}</a> 
  </div>

  
  const activationUrl = getDomainCfg().product.activationUrl;
  const activationLinkText = <div style={{width:"100%"}} className='text-center'>
    <h3>{translate('view-activation-question', productName)}</h3>
    <a href={activationUrl} >
      {translate('view-activation-link')}
      <button className="redColorBtn btn btn-primary btn-sm btn-go">Activate</button>
      <br/>
      <small>{translate('view-activation-subtext')}</small>
    </a>
  </div>

  const showSchoolUrl = schoolVersion;  
  const schoolText = <div style={{width:"100%"}} className='text-center'>
    <a href={activationUrl} >{translate('view-school-text')}</a>
  </div>

  const registrationUrl = getDomainCfg().product.registrationUrl || "https://app.vibeventure.de/registrierung/";
  const hasRegistrationUrl = typeof getDomainCfg().product.registrationUrl === 'string' && getDomainCfg().product.registrationUrl.trim().length > 0;
  const showRegistrationFunnelLink = (!schoolVersion && activationUrl === undefined && hasRegistrationUrl);

  const registrationFunnelLink = <div style={{width:"100%"}} className='text-center'>    
    <a href={registrationUrl} target="_blank" rel="noreferrer">
      {translate("view-login-register-funnel")}
    </a>
  </div>

  const showRegister = (getDomainCfg().viewSettings.features.registrationAvailable && !schoolVersion && activationUrl === undefined && !showRegistrationFunnelLink);
  const footer = (<>
    { showRegister && register }
    { showRegistrationFunnelLink && registrationFunnelLink }
    { showPrivateUrl && privateLinkText }
    { showActivationUrl && activationLinkText }
    { showSchoolUrl && schoolText }
    { !activationUrl && !schoolVersion && cancelButton }
  </>);

  return (<>
    <BaseModal
      childBody={body}
      footer={footer}
      footerCentered={true}
      show= {modalState?.data?.show}
      handleClose={abortRequested}
      closeOnBackdrop={false}
      closingDisabled={ activationUrl !== undefined || schoolVersion}
      />
  </>
  );
};
